import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import type {
  AuthScheme,
  IntegrationBusinessUnit,
  IntegrationValidationError,
  WebhookBulkUpdateRequestValues,
  WebhookBulkUpdateResponse
} from 'api';
import {
  getGetIntegrationWebhooksQueryKey,
  usePostIntegrationWebhooksBulkUpdate
} from 'api';
import type { BulkEditWebhookFormValues } from 'validators/bulkEditWebhookForm';
import { bulkEditWebhookFormSchema } from 'validators/bulkEditWebhookForm';

import { useOrganisationId } from 'hooks/context';

import { bulkOutcomeToast, summarizeBulkUpdate } from './bulkOutcome';
import { BULK_EDIT_GENERIC_ERROR } from './constants';
import { bulkEditSuccessMessage } from './utils';

import { useAuthenticationKeys } from '../hooks/useAuthenticationKeys';
import { useBusinessUnits } from '../hooks/useBusinessUnits';
import { extractIntegrationValidationError } from '../utils/extractIntegrationValidationError';

type UseBulkEditWebhooksFormArgs = {
  uuids: string[];
  onSaved: () => void;
};

export type UseBulkEditWebhooksForm = {
  form: UseFormReturn<BulkEditWebhookFormValues>;
  businessUnits: IntegrationBusinessUnit[];
  authKeys: AuthScheme[];
  hasAnyValue: boolean;
  submit: ReturnType<UseFormReturn<BulkEditWebhookFormValues>['handleSubmit']>;
  isSaving: boolean;
};

const buildValues = (
  values: BulkEditWebhookFormValues
): WebhookBulkUpdateRequestValues => {
  const patch: WebhookBulkUpdateRequestValues = {};

  const endpointUrl = values.endpointUrl?.trim();
  if (endpointUrl) {
    patch.destination_url = endpointUrl;
  }

  if (values.businessUnitUuid) {
    patch.business_unit_uuid = values.businessUnitUuid;
  }

  if (values.xAuthUuid) {
    patch.auth_uuid = values.xAuthUuid;
  }

  return patch;
};

export const useBulkEditWebhooksForm = ({
  uuids,
  onSaved
}: UseBulkEditWebhooksFormArgs): UseBulkEditWebhooksForm => {
  const organisationId = useOrganisationId<number>();
  const queryClient = useQueryClient();
  const openNotification = useOpenNotification();
  const bulkUpdateMutation = usePostIntegrationWebhooksBulkUpdate();

  const { keys: authKeys } = useAuthenticationKeys();
  const { businessUnits } = useBusinessUnits();

  const form = useForm<BulkEditWebhookFormValues>({
    mode: 'onChange',
    resolver: yupResolver(bulkEditWebhookFormSchema),
    defaultValues: {
      endpointUrl: '',
      businessUnitUuid: undefined,
      xAuthUuid: undefined
    }
  });

  const [endpointUrl, businessUnitUuid, xAuthUuid] = form.watch([
    'endpointUrl',
    'businessUnitUuid',
    'xAuthUuid'
  ]);
  const hasAnyValue = Boolean(
    endpointUrl?.trim() || businessUnitUuid || xAuthUuid
  );

  const handleSuccess = (response: WebhookBulkUpdateResponse) => {
    queryClient.invalidateQueries({
      queryKey: getGetIntegrationWebhooksQueryKey(organisationId)
    });
    openNotification(
      bulkOutcomeToast(summarizeBulkUpdate(response), {
        allSuccess: bulkEditSuccessMessage(uuids.length),
        verb: 'updated'
      })
    );
    onSaved();
  };

  const handleError = (error: IntegrationValidationError | null) => {
    openNotification({
      kind: 'error',
      message: extractIntegrationValidationError(error, BULK_EDIT_GENERIC_ERROR)
    });
  };

  const submit = form.handleSubmit((values) =>
    bulkUpdateMutation.mutate(
      { organisationId, data: { uuids, values: buildValues(values) } },
      { onSuccess: handleSuccess, onError: handleError }
    )
  );

  return {
    form,
    businessUnits,
    authKeys,
    hasAnyValue,
    submit,
    isSaving: bulkUpdateMutation.isPending
  };
};
